/**
 * HeroCard — shared aspect. Dark forest CTA hero with optional
 * day-countdown rail. Used by /brand Aspect 19 AND by the student
 * program dashboard's UpNextCard (phase-aware: build-your-team,
 * up-next-deliverable, judging-in-progress, final-results).
 *
 * Signature is intentionally fixed (FOREST surface, LINEN title,
 * AMBER CTA) — this is a marquee element, not a tonally-skinnable
 * variant card. theme.sidebarBg gives subtle per-pane variation
 * (FOREST on Student/Client/Admin, FOREST_DEEP on General).
 */
import {
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import { COLORS, RADII } from "../../lib/brand";
import type { PaneTheme } from "../brand/paneThemes";

export type HeroCardCountdown = {
  days: number;
  urgent: boolean;
};

export type HeroCardProps = {
  theme: PaneTheme;
  overline: string;
  title: string;
  subtitle: string;
  ctaLabel: string;
  onPress: () => void;
  countdown?: HeroCardCountdown | null;
  accessibilityLabel?: string;
};

export function HeroCard({
  theme,
  overline,
  title,
  subtitle,
  ctaLabel,
  onPress,
  countdown,
  accessibilityLabel,
}: HeroCardProps) {
  const isWeb = Platform.OS === "web";
  return (
    <View
      style={[
        {
          backgroundColor: theme.sidebarBg,
          borderRadius: RADII.lg,
          paddingVertical: 26,
          paddingHorizontal: 28,
          flexDirection: "row",
          alignItems: "center",
          gap: 24,
          overflow: "hidden",
          position: "relative",
        },
        isWeb
          ? ({
              backgroundImage:
                "radial-gradient(ellipse 600px 320px at 90% 20%, rgba(181,135,58,0.14) 0%, transparent 60%)",
            } as unknown as ViewStyle)
          : null,
      ]}
    >
      {/* LEFT — overline + title + subtitle + CTA pill */}
      <View style={{ flex: 1, minWidth: 0 }}>
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 10,
            marginBottom: 8,
          }}
        >
          <View style={{ width: 24, height: 1, backgroundColor: COLORS.AMBER.hex }} />
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 10,
              letterSpacing: 2.1,
              textTransform: "uppercase",
              color: COLORS.AMBER.hex,
            }}
          >
            {overline}
          </Text>
        </View>
        <Text
          style={{
            fontFamily: theme.displayMediumFont,
            fontSize: 26,
            lineHeight: 30,
            letterSpacing: -0.1,
            color: COLORS.LINEN.hex,
            marginBottom: 6,
          }}
          numberOfLines={2}
        >
          {title}
        </Text>
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 13,
            lineHeight: 19,
            color: "rgba(247,244,238,0.85)",
            marginBottom: 16,
          }}
          numberOfLines={2}
        >
          {subtitle}
        </Text>

        <Pressable
          onPress={onPress}
          accessibilityRole="button"
          accessibilityLabel={accessibilityLabel ?? ctaLabel}
          style={({ hovered }: { hovered?: boolean }) => [
            {
              alignSelf: "flex-start",
              paddingVertical: 10,
              paddingHorizontal: 18,
              borderRadius: RADII.sm,
              backgroundColor: hovered ? COLORS.AMBER_DEEP.hex : COLORS.AMBER.hex,
            },
            isWeb
              ? ({
                  cursor: "pointer",
                  transition: "background-color 0.18s ease",
                } as unknown as ViewStyle)
              : null,
          ]}
        >
          <Text
            style={{
              fontFamily: theme.bodyBoldFont,
              fontSize: 11,
              letterSpacing: 1.76,
              textTransform: "uppercase",
              color: COLORS.FOREST.hex,
            }}
          >
            {ctaLabel}
          </Text>
        </Pressable>
      </View>

      {/* RIGHT — big day-countdown stack (only when there's a real countdown) */}
      {countdown ? (
        <View style={{ alignItems: "flex-end", flexShrink: 0, minWidth: 88 }}>
          <Text
            style={{
              fontFamily: theme.displayFont,
              fontSize: 76,
              lineHeight: 78,
              letterSpacing: -2,
              color: countdown.urgent ? COLORS.AMBER.hex : COLORS.AMBER_WARM.hex,
            }}
          >
            {countdown.days}
          </Text>
          <Text
            style={{
              marginTop: 2,
              fontFamily: theme.bodyBoldFont,
              fontSize: 10.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: theme.textOnSidebarDim,
            }}
          >
            {countdown.days === 1 ? "Day left" : "Days left"}
          </Text>
        </View>
      ) : null}
    </View>
  );
}
